import React, { useEffect, useRef, useState } from "react";
import {
  Menu,
  X,
  Globe,
  Check,
  ShoppingBag,
  User,
  LogOut,
  Package,
  Briefcase,
  ChevronDown,
  Moon,
  SunMedium,
} from "lucide-react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useLanguage } from "@/context/LanguageContext";
import { useCart } from "@/context/CartContext";
import { useCustomerAuth } from "@/context/CustomerAuthContext";
import { useTheme } from "@/context/ThemeContext";
import { LANG_ORDER, LANG_FULL, LANG_LABELS } from "@/data/translations";
import { LOGO_DARK, LOGO_LIGHT } from "@/data/branding";
import { useShippingOnlyVisible } from "@/hooks/useShippingOnlyVisible";

const THEME_COPY = {
  en: {
    appearance: "Appearance",
    light: "Light",
    dark: "Dark",
  },
  ar: {
    appearance: "المظهر",
    light: "فاتح",
    dark: "داكن",
  },
  fr: {
    appearance: "Apparence",
    light: "Clair",
    dark: "Sombre",
  },
};

// Compact auth entry — "Sign in" when logged out, first-name dropdown
// (My Account / My Orders / Log out) when logged in. Used on desktop
// inside the navbar; the mobile menu has its own larger version below.
function AuthMenu({ t, lang }) {
  const { customer, logout } = useCustomerAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  if (!customer) {
    return (
      <Link
        to="/login"
        className="chrome-focus-ring hidden md:inline-flex items-center gap-1.5 h-11 px-4 rounded-full border border-black/10 bg-white/90 text-sm font-medium text-[#111] hover:border-[#F37021] hover:text-[#F37021] hover:bg-white transition-colors dark:border-white/10 dark:bg-white/10 dark:text-white dark:hover:border-[#F8A056] dark:hover:bg-white/12 dark:hover:text-[#F8A056]"
        data-testid="nav-sign-in"
      >
        <User className="w-4 h-4" />
        {t.nav.signIn}
      </Link>
    );
  }

  const firstName = (customer.full_name || "").trim().split(/\s+/)[0] || t.nav.myAccount;
  return (
    <div ref={ref} className="hidden md:inline-block relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="chrome-focus-ring inline-flex items-center gap-1.5 h-11 px-4 rounded-full border border-black/10 bg-white/90 text-sm font-medium text-[#111] hover:border-[#F37021] hover:bg-white transition-colors dark:border-white/10 dark:bg-white/10 dark:text-white dark:hover:border-[#F8A056] dark:hover:bg-white/12"
        data-testid="nav-account-button"
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <User className="w-4 h-4" />
        <span className="max-w-[100px] truncate">{firstName}</span>
        <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div
          className={`absolute top-full mt-2 ${lang === "ar" ? "start-0" : "end-0"} min-w-[200px] rounded-2xl border border-black/10 bg-white py-1.5 z-50 chrome-soft-shadow-strong dark:border-white/10 dark:bg-[#121212]`}
          role="menu"
          data-testid="nav-account-menu"
        >
          <button type="button" onClick={() => { setOpen(false); navigate("/account"); }} className="chrome-focus-ring w-full text-start px-4 py-2.5 text-sm hover:bg-[#F37021]/5 hover:text-[#F37021] flex items-center gap-2.5 dark:text-white dark:hover:bg-[#1B1B1B] dark:hover:text-[#F8A056]" data-testid="nav-menu-account">
            <User className="w-4 h-4" />
            {t.nav.myAccount}
          </button>
          <button type="button" onClick={() => { setOpen(false); navigate("/orders"); }} className="chrome-focus-ring w-full text-start px-4 py-2.5 text-sm hover:bg-[#F37021]/5 hover:text-[#F37021] flex items-center gap-2.5 dark:text-white dark:hover:bg-[#1B1B1B] dark:hover:text-[#F8A056]" data-testid="nav-menu-orders">
            <Package className="w-4 h-4" />
            {t.nav.myOrders}
          </button>
          <button type="button" onClick={() => { setOpen(false); navigate("/financial"); }} className="chrome-focus-ring w-full text-start px-4 py-2.5 text-sm hover:bg-[#F37021]/5 hover:text-[#F37021] flex items-center gap-2.5 dark:text-white dark:hover:bg-[#1B1B1B] dark:hover:text-[#F8A056]" data-testid="nav-menu-financial">
            <Briefcase className="w-4 h-4" />
            {t.nav.myFinancial}
          </button>
          <div className="my-1 border-t border-black/5 dark:border-white/10" />
          <button type="button" onClick={() => { setOpen(false); logout(); navigate("/"); }} className="chrome-focus-ring w-full text-start px-4 py-2.5 text-sm hover:bg-red-50 hover:text-red-600 flex items-center gap-2.5 dark:text-white dark:hover:bg-red-500/12 dark:hover:text-red-300" data-testid="nav-menu-logout">
            <LogOut className="w-4 h-4" />
            {t.nav.logout}
          </button>
        </div>
      )}
    </div>
  );
}

export default function Navbar() {
  const { t, lang, setLang } = useLanguage();
  const { totalItems, openCart } = useCart();
  const { isDark, preference, setThemePreference } = useTheme();
  const [scrolled, setScrolled] = useState(false);
  const shippingOnlyVisible = useShippingOnlyVisible();
  const [open, setOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const [themeOpen, setThemeOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const onHome = location.pathname === "/";
  const isCustomerArea =
    location.pathname === "/account" ||
    location.pathname === "/orders" ||
    location.pathname === "/financial" ||
    location.pathname.startsWith("/financial/");
  const showPrimaryNav = !isCustomerArea;
  const showSolidChrome = !onHome || scrolled || open || isCustomerArea;
  const themeText = THEME_COPY[lang] || THEME_COPY.en;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const onClick = () => {
      setLangOpen(false);
      setThemeOpen(false);
    };
    if (langOpen || themeOpen) window.addEventListener("click", onClick);
    return () => window.removeEventListener("click", onClick);
  }, [langOpen, themeOpen]);

  const links = [
    { href: "#how", label: t.nav.howItWorks },
    { href: "#countries", label: t.nav.countries },
    { href: "#rates", label: t.nav.rates },
    { href: "/stock", label: t.nav.stockStore, route: true },
    ...(shippingOnlyVisible ? [{ href: "/shipping-only", label: t.nav.shippingOnly, route: true }] : []),
    { href: "#why", label: t.nav.why },
    { href: "#about", label: t.nav.team },
    { href: "#faq", label: t.nav.faq },
  ];

  const handleNavClick = (e, link) => {
    e.preventDefault();
    setOpen(false);
    if (link.route) {
      navigate(link.href);
      return;
    }
    const href = link.href;
    if (!onHome) {
      navigate("/" + href);
      return;
    }
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  useEffect(() => {
    if (onHome && location.hash) {
      const el = document.querySelector(location.hash);
      if (el) {
        setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 100);
      }
    }
  }, [onHome, location.hash]);

  const goToCountries = (e) => {
    if (e) e.preventDefault();
    setOpen(false);
    if (!onHome) {
      navigate("/#countries");
      return;
    }
    const el = document.querySelector("#countries");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const handleLangPick = (l) => {
    setLang(l);
    setLangOpen(false);
    setThemeOpen(false);
    setOpen(false);
  };

  const isRouteActive = (href) => {
    if (href === "/stock") return location.pathname === "/stock" || location.pathname.startsWith("/stock/");
    return location.pathname === href;
  };

  const logoSrc = isDark ? LOGO_DARK : LOGO_LIGHT;
  const currentThemeLabel = themeText[preference] || themeText.light;
  const themeLabel = `${themeText.appearance}: ${currentThemeLabel}`;
  const themeOptions = [
    { value: "light", label: themeText.light, icon: SunMedium },
    { value: "dark", label: themeText.dark, icon: Moon },
  ];
  const ThemeIcon = preference === "dark" ? Moon : SunMedium;

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        showSolidChrome
          ? "bg-white/88 chrome-surface border-b border-black/5 shadow-[0_10px_30px_rgba(10,10,10,0.08)] dark:border-white/10 dark:bg-[#080808]/88 dark:shadow-[0_10px_30px_rgba(0,0,0,0.45)]"
          : "bg-white/58 chrome-surface border-b border-white/35 dark:border-white/10 dark:bg-[#080808]/66"
      }`}
    >
      <div className="mx-auto flex h-[64px] max-w-[100rem] items-center gap-2 px-3 sm:px-4 md:h-[78px] lg:gap-3 lg:px-5 xl:px-6">
        <a
          href="#home"
          onClick={(e) => handleNavClick(e, { href: "#home" })}
          className="chrome-focus-ring flex shrink-0 items-center gap-2 rounded-full"
          data-testid="nav-logo-link"
        >
          <img src={logoSrc} alt="BOB Services" className="h-9 w-auto max-w-[128px] sm:h-10 sm:max-w-[140px] md:h-[52px] md:max-w-[152px] xl:max-w-none" />
        </a>

        {showPrimaryNav ? (
        <nav className="hidden min-w-0 flex-1 flex-nowrap items-center justify-center gap-5 px-1 lg:flex xl:gap-6 2xl:gap-7" aria-label="Primary">
          {links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              onClick={(e) => handleNavClick(e, l)}
              className={`chrome-focus-ring relative shrink-0 whitespace-nowrap rounded-full px-1 py-2 text-[15px] font-medium transition-colors after:absolute after:start-1 after:end-1 after:-bottom-0.5 after:h-0.5 after:rounded-full after:bg-[#F37021] after:transition-opacity ${
                l.route && isRouteActive(l.href)
                  ? "text-[#111] after:opacity-100 dark:text-white"
                  : "text-[#333] hover:text-[#F37021] after:opacity-0 hover:after:opacity-100 dark:text-[#D7E0F0] dark:hover:text-[#F8A056]"
              }`}
              data-testid={`nav-link-${l.href.replace(/[#/]/g, '') || 'home'}`}
            >
              {l.label}
            </a>
          ))}
        </nav>
        ) : (
        <div className="hidden min-w-0 flex-1 lg:block" aria-hidden="true" />
        )}

        <div className="ml-auto flex shrink-0 items-center gap-1 sm:gap-1.5 md:gap-2 xl:gap-2.5">
          <div className="relative hidden md:block" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              onClick={() => {
                setThemeOpen((o) => !o);
                setLangOpen(false);
              }}
              className="chrome-focus-ring inline-flex h-10 items-center gap-1.5 rounded-full border border-black/10 bg-white/90 px-2.5 text-sm font-medium text-[#111] transition-colors hover:border-[#F37021] hover:text-[#F37021] hover:bg-white xl:h-11 xl:gap-2 xl:px-4 dark:border-white/10 dark:bg-white/10 dark:text-white dark:hover:border-[#F8A056] dark:hover:bg-white/12 dark:hover:text-[#F8A056]"
              data-testid="nav-theme-toggle"
              aria-label={themeLabel}
              aria-haspopup="true"
              aria-expanded={themeOpen}
              title={themeLabel}
            >
              <ThemeIcon className="h-4 w-4" />
              <span className="hidden 2xl:inline">{currentThemeLabel}</span>
              <ChevronDown className={`hidden h-3.5 w-3.5 transition-transform 2xl:block ${themeOpen ? "rotate-180" : ""}`} />
            </button>
            {themeOpen && (
              <div
                className="absolute top-full mt-2 end-0 min-w-[184px] rounded-2xl border border-black/5 bg-white p-1.5 z-50 chrome-soft-shadow-strong dark:border-white/10 dark:bg-[#121212]"
                data-testid="nav-theme-menu"
              >
                {themeOptions.map(({ value, label, icon: Icon }) => {
                  const active = preference === value;
                  return (
                    <button
                      key={value}
                      type="button"
                      onClick={() => {
                        setThemePreference(value);
                        setThemeOpen(false);
                      }}
                      className={`chrome-focus-ring flex w-full items-center justify-between gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-colors ${
                        active
                          ? "bg-[#FDF1E8] text-[#C55810] dark:bg-[#1A1A1A] dark:text-[#F8A056]"
                          : "text-[#111] hover:bg-[#FDF1E8] hover:text-[#F37021] dark:text-white dark:hover:bg-[#1B1B1B] dark:hover:text-[#F8A056]"
                      }`}
                      data-testid={`nav-theme-${value}`}
                    >
                      <span className="flex items-center gap-2.5">
                        <Icon className="h-4 w-4" />
                        <span>{label}</span>
                      </span>
                      {active ? <Check className="h-4 w-4 text-[#F37021]" /> : null}
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Language picker (desktop) */}
          <div className="hidden md:block relative" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={() => {
                setLangOpen((o) => !o);
                setThemeOpen(false);
              }}
              className="chrome-focus-ring inline-flex h-10 items-center gap-1.5 rounded-full border border-black/10 bg-white/90 px-2.5 text-sm font-medium text-[#111] transition-colors hover:border-[#F37021] hover:text-[#F37021] hover:bg-white xl:h-11 xl:px-3.5 dark:border-white/10 dark:bg-white/10 dark:text-white dark:hover:border-[#F8A056] dark:hover:bg-white/12 dark:hover:text-[#F8A056]"
              data-testid="nav-lang-toggle"
              aria-label="Select language"
              aria-haspopup="true"
              aria-expanded={langOpen}
            >
              <Globe className="w-4 h-4" />
              <span>{LANG_LABELS[lang]}</span>
            </button>
            {langOpen && (
              <div
                className="absolute top-full mt-2 end-0 min-w-[168px] rounded-2xl border border-black/5 bg-white py-1.5 z-50 chrome-soft-shadow-strong dark:border-white/10 dark:bg-[#121212]"
                data-testid="nav-lang-menu"
              >
                {LANG_ORDER.map((l) => (
                  <button
                    key={l}
                    onClick={() => handleLangPick(l)}
                    className="chrome-focus-ring w-full flex items-center justify-between gap-3 px-4 py-2.5 text-sm font-medium text-[#111] hover:bg-[#FDF1E8] hover:text-[#F37021] transition-colors dark:text-white dark:hover:bg-[#1B1B1B] dark:hover:text-[#F8A056]"
                    data-testid={`nav-lang-${l}`}
                  >
                    <span>{LANG_FULL[l]}</span>
                    {l === lang && <Check className="w-4 h-4 text-[#F37021]" />}
                  </button>
                ))}
              </div>
            )}
          </div>

          <a
            href="/#countries"
            onClick={goToCountries}
            className="chrome-focus-ring hidden h-10 items-center gap-2 rounded-full bg-[#F37021] px-3.5 font-medium text-white transition-all hover:bg-[#E06015] hover:-translate-y-0.5 md:inline-flex xl:h-11 xl:px-5 shadow-[0_10px_24px_rgba(243,112,33,0.22)]"
            data-testid="nav-cta-start-shopping"
          >
            {t.nav.startShopping}
          </a>

          {/* Customer auth entry (desktop) */}
          <AuthMenu t={t} lang={lang} />

          {/* Cart icon */}
          <button
            type="button"
            onClick={openCart}
            className="chrome-focus-ring relative h-10 w-10 md:h-11 md:w-11 rounded-full border border-black/10 bg-white/90 hover:bg-white hover:border-[#F37021] flex items-center justify-center transition-colors text-[#111] dark:border-white/10 dark:bg-white/10 dark:text-white dark:hover:border-[#F8A056] dark:hover:bg-white/12"
            data-testid="nav-cart-button"
            aria-label={t.store?.cartTitle || "Cart"}
          >
            <ShoppingBag className="w-5 h-5" />
            {totalItems > 0 && (
              <span
                className="absolute -top-1 -end-1 min-w-[20px] h-[20px] px-1 rounded-full bg-[#F37021] text-white text-[10px] font-bold flex items-center justify-center"
                data-testid="nav-cart-count"
              >
                {totalItems}
              </span>
            )}
          </button>

          <button
            className="chrome-focus-ring lg:hidden h-10 w-10 md:h-11 md:w-11 rounded-full border border-black/10 bg-white/90 hover:bg-white flex items-center justify-center text-[#111] transition-colors dark:border-white/10 dark:bg-white/10 dark:text-white dark:hover:bg-white/12"
            onClick={() => setOpen((o) => !o)}
            data-testid="nav-mobile-toggle"
            aria-label="Toggle menu"
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      <div
        className={`lg:hidden overflow-hidden transition-[max-height,opacity] duration-300 bg-white border-t border-black/5 shadow-[0_18px_40px_rgba(10,10,10,0.08)] dark:border-white/10 dark:bg-[#111111] dark:shadow-[0_18px_40px_rgba(0,0,0,0.36)] ${
          open ? "max-h-[calc(100vh-66px)] opacity-100" : "max-h-0 opacity-0"
        }`}
        data-testid="nav-mobile-panel"
      >
        <div className="max-h-[calc(100vh-66px)] overflow-y-auto px-5 pt-4 pb-[calc(env(safe-area-inset-bottom)+1.25rem)] flex flex-col gap-1.5">
          {showPrimaryNav ? links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              onClick={(e) => handleNavClick(e, l)}
                className={`chrome-focus-ring rounded-2xl px-3 py-3 text-base font-medium transition-colors ${
                l.route && isRouteActive(l.href)
                  ? "bg-[#FDF1E8] text-[#C55810] dark:bg-[#1F1F1F] dark:text-[#F8A056]"
                  : "text-[#111] hover:bg-[#FAFAFA] dark:text-white dark:hover:bg-[#171717]"
              }`}
              data-testid={`nav-mobile-link-${l.href.replace(/[#/]/g, '') || 'home'}`}
            >
              {l.label}
            </a>
          )) : null}

          {/* Language pills */}
          <div className="pt-4">
            <div className="grid grid-cols-3 gap-2">
              {LANG_ORDER.map((l) => (
                <button
                  key={l}
                  onClick={() => handleLangPick(l)}
                  className={`chrome-focus-ring min-w-0 h-11 rounded-full px-2 text-[13px] font-medium transition-all ${
                    l === lang
                      ? "bg-[#F37021] text-white"
                      : "bg-white border border-black/10 text-[#111] hover:border-[#F37021] hover:text-[#F37021] dark:border-white/10 dark:bg-white/10 dark:text-white dark:hover:border-[#F8A056] dark:hover:text-[#F8A056]"
                  }`}
                  data-testid={`nav-mobile-lang-${l}`}
                >
                  {LANG_FULL[l]}
                </button>
              ))}
            </div>
          </div>

          <a
            href="/#countries"
            onClick={goToCountries}
            className="chrome-focus-ring mt-3 h-12 rounded-full bg-[#F37021] text-white font-medium flex items-center justify-center shadow-[0_10px_24px_rgba(243,112,33,0.22)]"
            data-testid="nav-mobile-cta-start-shopping"
          >
            {t.nav.startShopping}
          </a>

          <div className="mt-2 rounded-[24px] border border-black/10 bg-[#FAFAFA] p-3 dark:border-white/10 dark:bg-[#111111]">
            <div className="mb-3 text-[11px] font-semibold uppercase tracking-[0.2em] text-[#5d6674] dark:text-[#BAC4D4]">
              {themeText.appearance}
            </div>
            <div className="grid grid-cols-2 gap-2">
              {themeOptions.map(({ value, label, icon: Icon }) => {
                const active = preference === value;
                return (
                  <button
                    key={value}
                    type="button"
                    onClick={() => setThemePreference(value)}
                    className={`chrome-focus-ring flex min-w-0 flex-col items-center justify-center gap-1 rounded-2xl px-2 py-2.5 text-center text-[12px] font-medium transition-all ${
                      active
                        ? "bg-[#F37021] text-white shadow-[0_12px_24px_rgba(243,112,33,0.22)]"
                        : "border border-black/10 bg-white text-[#111] hover:border-[#F37021] hover:text-[#F37021] dark:border-white/10 dark:bg-[#121212] dark:text-white dark:hover:border-[#F8A056] dark:hover:text-[#F8A056]"
                    }`}
                    data-testid={`nav-mobile-theme-${value}`}
                    aria-label={`${themeText.appearance}: ${label}`}
                  >
                    <Icon className="h-4 w-4 shrink-0" />
                    <span className="w-full truncate">{label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Customer auth (mobile) */}
          <MobileAuthLinks t={t} />
        </div>
      </div>
    </header>
  );
}

// Mobile auth links, rendered inside the mobile menu drawer.
function MobileAuthLinks({ t }) {
  const { customer, logout } = useCustomerAuth();
  const navigate = useNavigate();
  if (!customer) {
    return (
      <Link
        to="/login"
        className="chrome-focus-ring mt-2 h-12 rounded-full border border-black/10 text-[#111] font-medium flex items-center justify-center gap-2 hover:border-[#F37021] hover:text-[#F37021] transition-colors dark:border-white/10 dark:text-white dark:hover:border-[#F8A056] dark:hover:text-[#F8A056]"
        data-testid="nav-mobile-sign-in"
      >
        <User className="w-4 h-4" /> {t.nav.signIn}
      </Link>
    );
  }
  const firstName = (customer.full_name || "").trim().split(/\s+/)[0] || t.nav.myAccount;
  return (
    <div className="mt-3 pt-3 border-t border-black/10 space-y-1 dark:border-white/10">
      <Link
        to="/account"
        className="chrome-focus-ring flex min-w-0 items-center justify-between gap-3 rounded-2xl border border-black/10 bg-[#FAFAFA] px-3 py-3 text-sm font-medium text-[#111] transition-colors hover:border-[#F37021] hover:text-[#F37021] dark:border-white/10 dark:bg-[#111111] dark:text-white dark:hover:border-[#F8A056] dark:hover:text-[#F8A056]"
        data-testid="nav-mobile-account-name"
      >
        <span className="flex min-w-0 items-center gap-2.5">
          <User className="w-4 h-4 shrink-0" />
          <span className="truncate">{firstName}</span>
        </span>
        <ChevronDown className="w-4 h-4 -rotate-90 shrink-0" />
      </Link>
      <Link to="/account" className="chrome-focus-ring flex items-center gap-2.5 px-3 py-2.5 rounded-xl hover:bg-[#F37021]/5 text-sm text-[#111] transition-colors dark:text-white dark:hover:bg-[#1B1B1B]" data-testid="nav-mobile-account">
        <User className="w-4 h-4" /> {t.nav.myAccount}
      </Link>
      <Link to="/orders" className="chrome-focus-ring flex items-center gap-2.5 px-3 py-2.5 rounded-xl hover:bg-[#F37021]/5 text-sm text-[#111] transition-colors dark:text-white dark:hover:bg-[#1B1B1B]" data-testid="nav-mobile-orders">
        <Package className="w-4 h-4" /> {t.nav.myOrders}
      </Link>
      <Link to="/financial" className="chrome-focus-ring flex items-center gap-2.5 px-3 py-2.5 rounded-xl hover:bg-[#F37021]/5 text-sm text-[#111] transition-colors dark:text-white dark:hover:bg-[#1B1B1B]" data-testid="nav-mobile-financial">
        <Briefcase className="w-4 h-4" /> {t.nav.myFinancial}
      </Link>
      <button type="button" onClick={() => { logout(); navigate("/"); }} className="chrome-focus-ring w-full flex items-center gap-2.5 px-3 py-2.5 rounded-xl hover:bg-red-50 hover:text-red-600 text-sm text-[#111] transition-colors dark:text-white dark:hover:bg-red-500/12 dark:hover:text-red-300" data-testid="nav-mobile-logout">
        <LogOut className="w-4 h-4" /> {t.nav.logout}
      </button>
    </div>
  );
}
